<template>
    <el-form :model="form"
             ref="form"
             size="mini"
             :rules="$options.rules"
             hide-required-asterisk
             :show-message="false">
        <div class="col-form-items">
            <el-form-item class="form-item" prop="moduleNormal" :label-width="labelWidth.col1">
                <span slot="label">Module<sub>n</sub></span>
                <el-input v-model="form.moduleNormal" class="form-field" @change="handleToolChange">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" prop="pressureAngleNormal" :label-width="labelWidth.col2">
                <span slot="label">Pressure angle<sub>n</sub></span>
                <el-input v-model="form.pressureAngleNormal" class="form-field" @change="handleToolChange">
                    <template slot="suffix">{{units.angle}}</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" prop="helixAngle" :label-width="labelWidth.col1">
                <span slot="label">Helix angle</span>
                <el-input v-model="form.helixAngle" class="form-field" @change="handleToolChange">
                    <template slot="suffix">{{units.angle}}</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" prop="numberOfTeeth" :label-width="labelWidth.col2">
                <span slot="label">Number of teeth</span>
                <el-input v-model="form.numberOfTeeth" class="form-field" @change="handleToolChange"/>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Addendum coeff." prop="addendumCoefficient" :label-width="labelWidth.col1">
                <el-input v-model="form.addendumCoefficient" class="form-field" @change="handleToolChange"/>
            </el-form-item>
            <el-form-item class="form-item" label="Dedendum coeff." prop="dedendumCoefficient" :label-width="labelWidth.col2">
                <el-input v-model="form.dedendumCoefficient" class="form-field" @change="handleToolChange"/>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Profile shift coeff." prop="profileShiftCoefficient" :label-width="labelWidth.col1">
                <el-input v-model="form.profileShiftCoefficient" class="form-field" @change="handleToolChange"/>
            </el-form-item>
            <el-form-item class="form-item" prop="protuberanceSize" :label-width="labelWidth.col2">
                <span slot="label">Protuberance size<sub>n</sub></span>
                <el-input v-model="form.protuberanceSize" class="form-field" @change="handleToolChange">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Tip radius coeff." prop="tipRadiusCoefficient" :label-width="labelWidth.col1">
                <el-input v-model="form.tipRadiusCoefficient" class="form-field" @change="handleToolChange" :disabled="form.tipRadiusMax"/>
            </el-form-item>
            <el-form-item class="form-item" label="Max tip radius" prop="tipRadiusMax" :label-width="labelWidth.col2">
                <el-checkbox v-model="form.tipRadiusMax" class="form-field" @change="emitChangeTipMax"/>
            </el-form-item>
        </div>
        <h5>Computed Tooth Form</h5>
        <div class="col-form-items">
            <el-form-item class="form-item" label="Root diameter" prop="rootDiameter" :label-width="labelWidth.col1">
                <el-input v-model="form.rootDiameter" class="form-field" @change="handleToothChange">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
            <el-form-item class="form-item" label="Tip diameter" prop="tipDiameter" :label-width="labelWidth.col2">
                <el-input v-model="form.tipDiameter" class="form-field" @change="handleToothChange">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <div class="col-form-items">
            <el-form-item class="form-item" prop="toothThicknessNormal" :label-width="labelWidth.col1">
                <span slot="label">Tooth thickness<sub>n</sub></span>
                <el-input v-model="form.toothThicknessNormal" class="form-field" @change="handleToothChange">
                    <template slot="suffix">mm</template>
                </el-input>
            </el-form-item>
        </div>
        <h5>Notes</h5>
        <ol>
            <li>Addendum and dedendum are relative to the tool reference diameter.</li>
            <li>Below the base circle, a radial profile is assumed and no root fillet.</li>
            <li>Shaper tooth thickness will vary over time as the cutter becomes worn.</li>
            <li>Shaping is analogous to hobbing when shaper tooth count is very high.</li>
        </ol>
    </el-form>
</template>

<script>
  import { rules } from "../../assets/js/validators";
  import math from "../../assets/js/math"
  import gear from "../../assets/js/gear"

  export default {
    name: "FormShaperGeom",
    props: {
      form: Object,
      labelWidth: Object,
      units: Object,
    },
    rules: {
      addendumCoefficient: rules.requiredPositiveNumber,
      dedendumCoefficient: rules.requiredPositiveNumber,
      helixAngle: rules.requiredNumber,
      moduleNormal: rules.requiredPositiveNumber,
      numberOfTeeth: rules.requiredPositiveNumber,
      pressureAngleNormal: rules.requiredPositiveNumber,
      profileShiftCoefficient: rules.requiredNumber,
      protuberanceSize: rules.requiredZeroPositiveNumber,
      tipRadiusCoefficient: rules.requiredZeroPositiveNumber,
      // tooth form fields
      toothThicknessNormal: rules.requiredPositiveNumber,
      rootDiameter: rules.requiredPositiveNumber,
      tipDiameter: rules.requiredPositiveNumber,
    },
    methods: {
      updateToothForm() {
        // Compute tooth parameters and update fields: toothThicknessNormal, rootDiameter, tipDiameter

        // shaper tooth params
        const moduleNormal = this.form.moduleNormal;
        const numberOfTeeth = this.form.numberOfTeeth;
        const helixAngle = math.angle2rad(this.form.helixAngle, this.units.angle);
        const pressureAngleNormal = math.angle2rad(this.form.pressureAngleNormal, this.units.angle);
        const toothDedendum = gear.fromCoefficient(this.form.dedendumCoefficient, moduleNormal);
        const toothAddendum = gear.fromCoefficient(this.form.addendumCoefficient, moduleNormal);

        // computed shaper params
        const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
        const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});

        // update form fields
        this.form.toothThicknessNormal = gear.toothThicknessNormal2({moduleNormal, pressureAngleNormal,
          profileShiftCoefficient: this.form.profileShiftCoefficient
        });
        this.form.rootDiameter = gear.rootDiameter3({theoreticalPitchDiameter, toothDedendum});
        this.form.tipDiameter = gear.tipDiameter3({theoreticalPitchDiameter, toothAddendum})
      },
      updateToolData() {
        // Compute tool parameters and update fields: profileShiftCoefficient, dedendumCoefficient, addendumCoefficient

        // shaper tooth params
        const helixAngle = math.angle2rad(this.form.helixAngle, this.units.angle);
        const pressureAngleNormal = math.angle2rad(this.form.pressureAngleNormal, this.units.angle);
        const [moduleNormal, numberOfTeeth, rootDiameter, tipDiameter] = [this.form.moduleNormal,
          this.form.numberOfTeeth, this.form.rootDiameter, this.form.tipDiameter].map(Number);

        // computed shaper params
        const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
        const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
        const toothDedendum = gear.toothDedendum1({theoreticalPitchDiameter, rootDiameter});
        const toothAddendum = gear.toothAddendum1({theoreticalPitchDiameter, tipDiameter});

        // update form fields
        this.form.profileShiftCoefficient = gear.profileShiftCoefficient2({moduleNormal, pressureAngleNormal,
          helixAngle, toothThicknessNormal: this.form.toothThicknessNormal});
        this.form.dedendumCoefficient = gear.coefficient(toothDedendum, moduleNormal);
        this.form.addendumCoefficient = gear.coefficient(toothAddendum, moduleNormal);
      },
      handleToolChange() {
        // Update tooth form, then emit blur.
        this.updateToothForm();
        this.emitChange();
      },
      handleToothChange() {
        // Update tool data, then emit blur.
        this.updateToolData();
        this.emitChange();
      },
      emitChange() {
        // @change event triggers only if the input has changed when the box loses focus or the Enter key is pressed

        this.$refs.form.validate((valid) => {
          this.$emit('change', valid);
        });
      },
      emitChangeTipMax(val) {
        // arg: boolean to indicate if max tool tip radius is checked.

        this.$refs.form.validate((valid) => {
          this.$emit('change-tip-max', valid, val)
        });
      },
      validate() {
        return this.$refs.form.validate();
      },
    },
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped>
    ol > li {
        font-size: 0.8rem;
    }
</style>
